<script setup lang="ts">
import { computed } from "vue";
import { toast } from "@/design/composables/use-toast";
import SelectField from "@/design/primitives/SelectField.vue";
import TextField from "@/design/primitives/TextField.vue";
import {
	type Presence,
	useDeclarePresence,
	usePresence,
} from "@/features/workspace/api/use-presence";
import { useProfile } from "@/features/workspace/api/use-workspace";
import { useWorkspaceI18n } from "../i18n/use-workspace-i18n";

/**
 * "Saí" e "não me passe mais que isto", onde qualquer pessoa alcança.
 *
 * ── POR QUE UM SEGUNDO CONTROLE, SE `TeamPresence.vue` JÁ DESENHA ISTO ─────
 *
 * Porque o outro é mudo por desenho — recebe a presença por prop e devolve a
 * declaração por emit — e mora em `pages/`, sob uma rota `minRole: admin`. Um
 * atendente é justamente quem precisa declarar ausência e é justamente quem não
 * abre /team. Reusar o arquivo de lá seria `features/` importando `pages/`, que a
 * camada proíbe; então este busca sozinho e não pede nada ao pai.
 *
 * Quem sou eu vem do `useProfile()` desta mesma feature, e não da sessão: uma
 * feature não importa outra, e o `id` do perfil é o mesmo `id` da linha de presença.
 *
 * O servidor sempre esteve aberto (`PATCH /v3/workspace/presence` não tem
 * `@MinRole`); o que faltava era o lugar.
 */
const { data: profile } = useProfile();
const { data: presence } = usePresence();
const declare = useDeclarePresence();
const { t } = useWorkspaceI18n();

const mine = computed<Presence | undefined>(() =>
	profile.value ? (presence.value ?? []).find((row) => row.id === profile.value?.id) : undefined,
);

// The VALUES are the stored states; only their words follow the panel.
const DECLARED_OPTIONS = computed(() =>
	(["available", "busy", "away"] as const).map((value) => ({
		value,
		label: t(`presence.${value}`),
	})),
);

/** "3 conversations of 6", or just the load, when nobody declared a ceiling. */
const load = computed(() => {
	const row = mine.value;
	if (!row) return "";
	return row.maxConcurrent === null
		? t("presence.conversations", { n: row.openConversations }, row.openConversations)
		: t(
				"presence.conversationsOf",
				{ n: row.openConversations, max: row.maxConcurrent },
				row.openConversations,
			);
});

async function save(patch: {
	declaredState?: Presence["declaredState"];
	maxConcurrent?: number | null;
}): Promise<void> {
	try {
		await declare.mutateAsync(patch);
	} catch (e) {
		// A menu has nowhere to park a standing error sentence; the toast is the one
		// voice this screen already uses for "did not save".
		toast.error(e);
	}
}

/** An empty field means "no ceiling", which is a choice and not an error. */
function saveCeiling(raw: string): void {
	const value = raw.trim() === "" ? null : Number(raw);
	if (value !== null && (!Number.isInteger(value) || value < 1 || value > 999)) return;
	if (value === (mine.value?.maxConcurrent ?? null)) return;
	void save({ maxConcurrent: value });
}
</script>

<template>
	<div v-if="mine" class="flex flex-col gap-2">
		<p class="text-xs text-fg-subtle">
			{{ t("presence.mine") }}
			<span v-if="load">· {{ load }}</span>
		</p>
		<SelectField
			:model-value="mine.declaredState"
			:disabled="declare.isPending.value"
			:aria-label="t('presence.state')"
			:options="DECLARED_OPTIONS"
			@update:model-value="save({ declaredState: $event as Presence['declaredState'] })"
		/>
		<label class="flex items-center gap-1.5 text-xs text-fg-subtle">
			{{ t("presence.atMost") }}
			<TextField
				type="number"
				min="1"
				max="999"
				:placeholder="t('presence.noCeiling')"
				:aria-label="t('presence.ceilingLabel')"
				class="w-24"
				:model-value="String(mine.maxConcurrent ?? '')"
				:disabled="declare.isPending.value"
				@change="saveCeiling(($event.target as HTMLInputElement).value)"
			/>
			conversas
		</label>
	</div>
</template>
